/**
 * File parsing utilities for coordinate data files
 *
 * Supports PNEZD format (Point, North, East, Elevation, Description)
 * with comma or space delimited fields.
 */

import type { SystemPoint, PointPair } from '@/types';
import { WEIGHT, normalizeWeight } from '@/logic/weights';

/** A line that could not be parsed, with the reason it was skipped. */
export interface SkippedLine {
  lineNumber: number;
  content: string;
  reason: string;
}

/** Validate a complete decimal coordinate without changing its written precision. */
export const isCoordinate = (value: string): boolean =>
  /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim()) &&
  Number.isFinite(Number(value));

/** CSV fields may contain quoted commas and escaped quotes. */
const splitRow = (line: string): string[] => {
  if (!line.includes(',')) return line.trim().split(/\s+/);
  const fields: string[] = [];
  let value = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        value += '"';
        i++;
      } else quoted = !quoted;
    } else if (char === ',' && !quoted) {
      fields.push(value.trim());
      value = '';
    } else value += char;
  }
  if (quoted) return [];
  fields.push(value.trim());
  return fields;
};

export interface ParseCoordinateResult {
  points: SystemPoint[];
  skipped: SkippedLine[];
}

/**
 * Parse coordinate data in PNEZD format (comma, space, or tab delimited)
 * with a per-line report of anything that was skipped and why.
 *
 * @param content - Raw text (file contents or pasted rows)
 * @param systemNumber - System number (1 or 2)
 */
export const parseCoordinateData = (
  content: string,
  systemNumber: 1 | 2
): ParseCoordinateResult => {
  const lines = content.split('\n');
  const points: SystemPoint[] = [];
  const skipped: SkippedLine[] = [];
  const identifiers = new Set<string>();

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    // Skip comment lines silently
    if (trimmed.startsWith('#') || trimmed.startsWith('//')) {
      return;
    }

    const parts = splitRow(line);
    if (
      /^(point|pt|point number)$/i.test(parts[0] ?? '') &&
      /^(north|northing)$/i.test(parts[1] ?? '') &&
      /^(east|easting)$/i.test(parts[2] ?? '')
    )
      return;

    if (parts.length < 3) {
      skipped.push({
        lineNumber: index + 1,
        content: trimmed,
        reason: 'needs at least Point, North, East columns',
      });
      return;
    }

    // PNEZD format: Point, North, East, [Elevation], [Description]
    const pointNum = parts[0];
    const north = parts[1];
    const east = parts[2];
    // parts[3] would be elevation (ignored)
    const desc = parts
      .slice(parts[3] === '' || isCoordinate(parts[3] ?? '') ? 4 : 3)
      .join(' ');

    if (!isCoordinate(north) || !isCoordinate(east)) {
      skipped.push({
        lineNumber: index + 1,
        content: trimmed,
        reason: 'North/East must be complete, finite numeric coordinates',
      });
      return;
    }
    if (!pointNum || (isCoordinate(pointNum) && Number(pointNum) <= 0)) {
      skipped.push({
        lineNumber: index + 1,
        content: trimmed,
        reason: 'point number must be positive',
      });
      return;
    }

    if (identifiers.has(pointNum)) {
      skipped.push({
        lineNumber: index + 1,
        content: trimmed,
        reason: `duplicate point identifier: ${pointNum}`,
      });
      return;
    }
    identifiers.add(pointNum);
    points.push({
      point: pointNum,
      north,
      east,
      description: desc,
      system: systemNumber,
    });
  });

  return { points, skipped };
};

/**
 * Parse a point pair file and build PointPair[] by looking up coordinates
 * in the already-loaded system points.
 *
 * Each row should contain two point numbers and an optional weight:
 *   pointA, pointB [, weight]
 *
 * The column order does not matter — each point is matched against both
 * systems automatically. A weight is snapped to Ignore/Free/Unity/Fixed
 * (e.g. 5 → Unity, 0.5 → Free); an empty weight means Unity. Rows whose
 * points are not loaded, or whose weight is not a finite number, are skipped.
 * Generated pair ids start at 1.
 */
export const parsePairFile = (
  content: string,
  sys1: SystemPoint[],
  sys2: SystemPoint[]
): { pairs: PointPair[]; skipped: number } => {
  const lines = content.split('\n').filter((line) => line.trim());
  const pairs: PointPair[] = [];
  let skipped = 0;
  let id = 1;

  for (const line of lines) {
    if (line.trim().startsWith('#') || line.trim().startsWith('//')) continue;

    const parts = splitRow(line);
    if (parts.length < 2) {
      skipped++;
      continue;
    }

    const [a, b, weightText = ''] = parts;
    const weight =
      weightText === '' ? WEIGHT.unity : normalizeWeight(weightText);
    if (weight === null) {
      skipped++;
      continue;
    }

    const a1 = sys1.find((p) => p.point === a);
    const b2 = sys2.find((p) => p.point === b);
    const a2 = sys2.find((p) => p.point === a);
    const b1 = sys1.find((p) => p.point === b);

    let s1: SystemPoint | undefined;
    let s2: SystemPoint | undefined;

    if (a1 && b2) {
      s1 = a1;
      s2 = b2;
    } else if (a2 && b1) {
      s1 = b1;
      s2 = a2;
    }

    if (!s1 || !s2) {
      skipped++;
      continue;
    }

    pairs.push({
      id: id++,
      label: `${s1.point}-${s2.point}`,
      sys1Point: s1.point,
      sys2Point: s2.point,
      n1: s1.north,
      e1: s1.east,
      n2: s2.north,
      e2: s2.east,
      weight,
      desc1: s1.description,
      desc2: s2.description,
    });
  }

  return { pairs, skipped };
};

/**
 * Export points to PNEZD format
 */
export const exportToPNEZD = (
  points: Array<{
    point: string;
    north: string;
    east: string;
    elevation?: string;
    description?: string;
  }>
): string => {
  return points
    .map((p) =>
      [p.point, p.north, p.east, p.elevation || '0.000', p.description || '']
        .map((field) =>
          /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field
        )
        .join(',')
    )
    .join('\n');
};

/**
 * Create a downloadable file from content
 */
export const downloadFile = (
  content: string,
  filename: string,
  mimeType = 'text/plain'
): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
